

// EditProperty.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import HomeService from "../../../Services/HomeService";
import toast from "react-hot-toast";
import HttpClientXml from "../../../utils/HttpClientXml";
import moment from "moment";
import { propertyUnits } from "../../../utils/Helper";

function EditProperty() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Main states
  const [editProperty, setEditProperty] = useState(null);
  const [propertyType, setPropertyType] = useState([]);
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [bhkOptions, setBhkOptions] = useState([]);
  const [facings, setFacings] = useState([]);
  const [floorOptions, setFloorOptions] = useState([]);
  const [images, setImages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [featureInput, setFeatureInput] = useState("");
  const [isNewEntry, setIsNewEntry] = useState(false);
  const [salesStaff, setSalesStaff] = useState([]);
  const [surveyStaff, setSurveyStaff] = useState([]);
  const [telecallingStaff, setTelecallingStaff] = useState([]);
  const [apartments, setApartments] = useState([]);

  // Contact related states
  const [contactType, setContactType] = useState("");
  const [contactName, setContactName] = useState("");

  // Loading states
  const [imageLoading, setImageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch property details
  useEffect(() => {
    fetchPropertyDetails();
    fetchDropdownData();
    fetchFacings();
  }, [id]);

  // Fetch all necessary dropdown data
  const fetchDropdownData = async () => {
    try {
      const [propertyTypes, citiesData] = await Promise.all([
        HomeService.getPropertyTypes(),
        HomeService.getCities(),
        // HomeService.getBHKs(),
        // HomeService.getFloors(),
      ]);
      setPropertyType(propertyTypes?.data || []);
      setCities(citiesData?.data || []);
      //   setBhkOptions(bhks?.data || []);
      //   setFloorOptions(floors?.data || []);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
      toast.error("Failed to load dropdown data");
    }
  };

  // Fetch facings
  const fetchFacings = async () => {
    try {
      const res = await HomeService.getFacings();
      if (res?.data) {
        setFacings(res.data);
      }
    } catch (error) {
      console.error("Error fetching facings:", error);
    }
  };

  // Fetch property details by ID
  const fetchPropertyDetails = async () => {
    setIsLoading(true);
    try {
      const response = await HomeService.getPropertyById(id);
      if (response?.data) {
        const property = response.data;

        // Format the property data for form
        const formattedProperty = {
          ...property,
          propertyType: property?.propertytype?._id,
          city: property?.city?._id,
          locality: property?.locality?._id,
          facing: property?.facing?._id,
          telecallerId: property?.telecaller?._id,
          surveyorId: property?.surveyor?._id,
          salesPersonId: property?.salesPerson?._id,
        };

        setEditProperty(formattedProperty);
        setFeatures(property?.features || []);
        setImages(property?.images || []);
        setContactType(property?.contactType || "");
        setContactName(property?.name || "");
        setIsNewEntry(property?.newEntry === "$");

        // Fetch related data based on property
        if (property?.city?._id) {
          fetchLocalities(property.city._id);
          fetchAllStaff(property.city._id);
          fetchApartmentsByLocation(property.city._id, property?.locality?._id);
        }
      }
    } catch (error) {
      console.error("Error fetching property:", error);
      toast.error("Failed to load property details");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch localities for a city
  const fetchLocalities = async (cityId) => {
    try {
      const res = await HomeService.getLocalities();
      if (res?.data) {
        setLocalities(res.data.filter((item) => item?.city?._id === cityId));
      }
    } catch (error) {
      console.error("Error fetching localities:", error);
    }
  };

  // Fetch all staff for a city
  const fetchAllStaff = async (cityId) => {
    try {
      const res = await HomeService.getStaffs();
      if (res?.data) {
        setTelecallingStaff(
          res.data.filter(
            (item) => item.role === "telecalling" && item?.city?._id === cityId,
          ),
        );
        setSurveyStaff(
          res.data.filter(
            (item) => item.role === "survey" && item?.city?._id === cityId,
          ),
        );
        setSalesStaff(
          res.data.filter(
            (item) => item.role === "sales" && item?.city?._id === cityId,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching staffs:", error);
    }
  };

  // Fetch apartments by city and locality
  const fetchApartmentsByLocation = async (cityId, localityId) => {
    try {
      const res = await HomeService.getApartment();
      if (res?.data) {
        setApartments(
          res.data.filter(
            (item) =>
              item?.city?._id === cityId && item?.locality?._id === localityId,
          ),
        );
      }
    } catch (error) {
      console.error("Error fetching apartments:", error);
    }
  };

  // Handle contact type change
  const handleContactTypeChange = (type) => {
    setContactType(type);
    if (!type) {
      setContactName("");
    }
  };

  // Get name field label based on contact type
  const getNameFieldLabel = () => {
    switch (contactType) {
      case "Owner":
        return "Owner Name";
      case "Builder":
        return "Builder Name";
      case "Dealer":
        return "Dealer Name";
      default:
        return "Name";
    }
  };

  // Get name field placeholder
  const getNameFieldPlaceholder = () => {
    switch (contactType) {
      case "Owner":
        return "Enter Owner Name";
      case "Builder":
        return "Enter Builder Name";
      case "Dealer":
        return "Enter Dealer Name";
      default:
        return "Select contact type first";
    }
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    const uploadedImages = [];

    for (let i = 0; i < files.length; i++) {
      const data = new FormData();
      data.append("image", files[i]);
      setImageLoading(true);
      try {
        const res = await HttpClientXml.fileUplode(
          "image-upload",
          "POST",
          data,
        );
        if (res?.image) {
          uploadedImages.push(res.image);
        }
      } catch (error) {
        toast.error("Failed to upload image: " + files[i].name);
        console.error("Error uploading image:", error);
      } finally {
        setImageLoading(false);
      }
    }

    if (uploadedImages.length > 0) {
      setImages((prevImages) => [...prevImages, ...uploadedImages]);
      toast.success("Images uploaded successfully!");
    }
  };

  // Handle remove image
  const handleRemoveImage = (imageToRemove) => {
    setImages((prevImages) =>
      prevImages.filter((img) => img !== imageToRemove),
    );
  };

  // Handle add feature
  const handleAddFeature = () => {
    if (featureInput.trim() !== "") {
      setFeatures((prevFeatures) => [...prevFeatures, featureInput.trim()]);
      setFeatureInput("");
    } else {
      toast.error("Feature cannot be empty");
    }
  };

  // Handle remove feature
  const handleRemoveFeature = (featureToRemove) => {
    setFeatures((prevFeatures) =>
      prevFeatures.filter((feature) => feature !== featureToRemove),
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate contact type and name
    if (contactType && !contactName.trim()) {
      toast.error(`Please enter ${getNameFieldLabel()}`);
      return;
    }

    if (!contactType && contactName.trim()) {
      toast.error("Please select contact type");
      return;
    }

    // Validate price and floor count
    if (editProperty?.floor && editProperty?.price) {
      const floorPlusCount = editProperty.floor.split("+").length - 1;
      const pricePlusCount = editProperty.price.split("+").length - 1;
      if (floorPlusCount !== pricePlusCount) {
        toast.error("Floor count and price count is not matching");
        return;
      }
    }

    // Prepare contact data
    let contactData = {};
    if (contactType && contactName.trim()) {
      contactData = {
        contactType: contactType,
        name: contactName.trim(),
      };
    }

    // Prepare property data for update
    const formattedProperty = {
      propertyType: editProperty?.propertyType,
      city: editProperty?.city,
      locality: editProperty?.locality,
      address: editProperty?.address,
      area: parseInt(editProperty?.area || 0),
      unit: editProperty?.unit,
      bhk: editProperty?.bhk,
      floor: editProperty?.floor,
      price: editProperty?.price,
      priceUnit: editProperty?.priceUnit,
      phoneNo: editProperty?.phoneNo,
      facing: editProperty?.facing,
      features: features,
      images: images,
      propertyFor: editProperty?.propertyFor,
      date: editProperty?.date,
      telecallerId: editProperty?.telecallerId,
      surveyorId: editProperty?.surveyorId,
      salesPersonId: editProperty?.salesPersonId,
      age: editProperty?.age,
      aptName: editProperty?.aptName,
      locationUrl: editProperty?.locationUrl,
      remarks: editProperty?.remarks,
      newEntry: isNewEntry ? "$" : "",
      chosen: editProperty?.chosen || "",
      ...contactData,
    };

    console.log("Updating property:", formattedProperty);

    try {
      setIsLoading(true);
      const res = await HomeService.updateProperty(id, formattedProperty);
      if (res?.status) {
        toast.success("Property updated successfully!");
        // Close the tab or redirect after successful update
        setTimeout(() => {
          window.close();
        }, 1500);
      }
    } catch (error) {
      toast.error("Failed to update property.");
      console.error("Error updating property:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setEditProperty({
      ...editProperty,
      [field]: value,
    });
  };

  // Handle city change
  const handleCityChange = (cityId) => {
    setEditProperty({
      ...editProperty,
      city: cityId,
      locality: "",
      aptName: "",
    });
    fetchLocalities(cityId);
    fetchAllStaff(cityId);
    setApartments([]);
  };

  // Handle locality change
  const handleLocalityChange = (localityId) => {
    setEditProperty({
      ...editProperty,
      locality: localityId,
      aptName: "",
    });
    if (editProperty?.city) {
      fetchApartmentsByLocation(editProperty.city, localityId);
    }
  };

  // If loading initial data
  if (isLoading && !editProperty) {
    return (
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading property details...</p>
        </div>
      </div>
    );
  }

  // If no property found
  if (!editProperty) {
    return (
      <div className="main_wrap" style={{ marginTop: "6rem" }}>
        <div className="container-fluid text-center">
          <h3>Property not found</h3>
          <button className="btn btn-primary mt-3" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="mb-0">Edit Property</h2>
          <button className="btn btn-secondary" onClick={() => window.close()}>
            <i className="fas fa-times me-2"></i>
            Close
          </button>
        </div>
        {/* <div className="alert alert-info">
          <i className="fas fa-info-circle me-2"></i>
          Editing property ID: <strong>{id}</strong>
        </div> */}

        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-lg-9">
              <div className="row">
                {/* Property Type */}
                <div className="col-md-6 mt-3">
                  <label>
                    Type<span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={editProperty?.propertyType || ""}
                    onChange={(e) =>
                      handleInputChange("propertyType", e.target.value)
                    }
                    disabled={isLoading}
                    required
                  >
                    <option value="">Select Property Type</option>
                    {propertyType.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.propertyType}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Property For */}
                <div className="col-md-6 mt-3">
                  <label>
                    For<span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={editProperty?.propertyFor || ""}
                    onChange={(e) =>
                      handleInputChange("propertyFor", e.target.value)
                    }
                    required
                    disabled={isLoading}
                  >
                    <option value="">--Select--</option>
                    <option value="sale">Sale</option>
                    <option value="rent">Rent</option>
                  </select>
                </div>

                {/* City */}
                <div className="col-md-6 mt-3">
                  <label>
                    City<span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={editProperty?.city || ""}
                    onChange={(e) => handleCityChange(e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">---Select City---</option>
                    {cities.map((city) => (
                      <option key={city._id} value={city._id}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Locality */}
                <div className="col-md-6 mt-3">
                  <label>
                    Locality<span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={editProperty?.locality || ""}
                    onChange={(e) => handleLocalityChange(e.target.value)}
                    required
                    disabled={isLoading || !editProperty?.city}
                  >
                    <option value="">---Select Locality---</option>
                    {localities.map((locality) => (
                      <option key={locality._id} value={locality._id}>
                        {locality.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Apartment/Building Name */}
                <div className="col-md-6 mt-3">
                  <label>Apartment/Building Name</label>
                  <select
                    className="form-control"
                    value={editProperty?.aptName || ""}
                    onChange={(e) =>
                      handleInputChange("aptName", e.target.value)
                    }
                    disabled={isLoading || !editProperty?.locality}
                  >
                    <option value="">---Select Apartment---</option>
                    {apartments.map((apartment) => (
                      <option key={apartment._id} value={apartment.aptName}>
                        {apartment.aptName}
                      </option>
                    ))}
                    <option value="other">
                      Other (Please specify in address)
                    </option>
                  </select>
                </div>

                {/* Age */}
                <div className="col-md-6 mt-3">
                  <label>Age</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editProperty?.age || ""}
                    onChange={(e) => handleInputChange("age", e.target.value)}
                    placeholder="Enter Age of the property"
                    disabled={isLoading}
                  />
                </div>

                {/* Contact Type */}
                <div className="col-md-12 mt-3">
                  <label>
                    Contact Type<span className="text-danger">*</span>
                  </label>
                  <div className="d-flex gap-4 mt-2">
                    {["Owner", "Builder", "Dealer"].map((type) => (
                      <div className="form-check" key={type}>
                        <input
                          className="form-check-input"
                          type="radio"
                          name="contactType"
                          id={type.toLowerCase()}
                          value={type}
                          checked={contactType === type}
                          onChange={() => handleContactTypeChange(type)}
                          disabled={isLoading}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={type.toLowerCase()}
                        >
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Name */}
                <div className="col-md-6 mt-3">
                  <label>
                    {getNameFieldLabel()}
                    {contactType && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    placeholder={getNameFieldPlaceholder()}
                    disabled={isLoading || !contactType}
                    required={!!contactType}
                  />
                </div>

                {/* Phone Number */}
                <div className="col-md-6 mt-3">
                  <label>
                    Phone Number<span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className="form-control"
                    value={editProperty?.phoneNo || ""}
                    onChange={(e) =>
                      handleInputChange("phoneNo", e.target.value)
                    }
                    placeholder="Enter Phone Number"
                    disabled={isLoading}
                    required
                  />
                </div>

                {/* Features */}
                <div className="col-md-6 mt-3">
                  <label>Features</label>
                  <div className="d-flex">
                    <input
                      type="text"
                      className="form-control"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      placeholder="Enter a feature"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="btn btn-secondary ms-2"
                      onClick={handleAddFeature}
                      disabled={isLoading}
                    >
                      Add
                    </button>
                  </div>
                  <div className="mt-2">
                    {features.map((feature, index) => (
                      <span
                        key={index}
                        className="badge badge-primary me-2 mb-2"
                        style={{
                          border: "solid 1px #85da4f",
                          backgroundColor: "transparent",
                          color: "#000",
                          padding: "8px 12px",
                          borderRadius: "13px",
                          fontSize: "14px",
                          cursor: "pointer",
                          boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                        }}
                        onClick={() =>
                          !isLoading && handleRemoveFeature(feature)
                        }
                      >
                        {feature}{" "}
                        <span
                          style={{
                            marginLeft: "5px",
                            fontWeight: "bold",
                            color: "#ff0000",
                          }}
                        >
                          ×
                        </span>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Facing */}
                <div className="col-md-6 mt-3">
                  <label>Facing</label>
                  <select
                    className="form-control"
                    value={editProperty?.facing || ""}
                    onChange={(e) =>
                      handleInputChange("facing", e.target.value)
                    }
                    disabled={isLoading}
                  >
                    <option value="">Select Facing</option>
                    {facings.map((facing) => (
                      <option key={facing._id} value={facing._id}>
                        {facing.facing}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Address */}
                <div className="col-md-6 mt-3">
                  <label>Address</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editProperty?.address || ""}
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="Enter full address"
                    disabled={isLoading}
                  />
                </div>

                {/* Location URL */}
                <div className="col-md-6 mt-3">
                  <label>Location URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editProperty?.locationUrl || ""}
                    onChange={(e) =>
                      handleInputChange("locationUrl", e.target.value)
                    }
                    placeholder="Enter Google Maps URL"
                    disabled={isLoading}
                  />
                </div>

                {/* Area */}
                <div className="col-md-4 mt-3">
                  <label>
                    Area<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editProperty?.area || ""}
                    onChange={(e) => handleInputChange("area", e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                {/* Unit */}
                <div className="col-md-2 mt-3">
                  <label>
                    Unit<span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-control"
                    value={editProperty?.unit || ""}
                    onChange={(e) => handleInputChange("unit", e.target.value)}
                    required
                    disabled={isLoading}
                  >
                    <option value="">--Select--</option>
                    {propertyUnits()?.areaUnits.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* BHK */}
                <div className="col-md-6 mt-3">
                  <label>BHK</label>
                  <select
                    className="form-control"
                    value={editProperty?.bhk || ""}
                    onChange={(e) => handleInputChange("bhk", e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">--Select--</option>
                    {propertyUnits()?.bhkTypes.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Floor */}
                <div className="col-md-6 mt-3">
                  <label>Floor</label>
                  <select
                    className="form-control"
                    value={editProperty?.floor || ""}
                    onChange={(e) => handleInputChange("floor", e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">--Select--</option>
                    {propertyUnits()?.floors.map((item) => (
                      <option key={item} value={item}>
                        {item}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Price */}
                <div className="col-md-4 mt-3">
                  <label>Price</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editProperty?.price || ""}
                    onChange={(e) => handleInputChange("price", e.target.value)}
                    disabled={isLoading}
                  />
                </div>

                {/* Price Unit */}
                <div className="col-md-2 mt-3">
                  <label>Price Unit</label>
                  <select
                    className="form-control"
                    value={editProperty?.priceUnit || ""}
                    onChange={(e) =>
                      handleInputChange("priceUnit", e.target.value)
                    }
                    disabled={isLoading}
                  >
                    <option value="">--Select--</option>
                    {propertyUnits()?.priceUnits.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Is the Property Basic (chosen) dropdown */}
                <div className="col-md-6 mt-3">
                  <label htmlFor="chosen">Is the Property Basic</label>
                  <select
                    id="chosen"
                    className="form-control"
                    value={editProperty?.chosen || ""}
                    onChange={(e) => handleInputChange("chosen", e.target.value)}
                    disabled={isLoading}
                  >
                    <option value="">--Select--</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {/* Images */}

                <div className="col-md-12 mt-3">
                  <label>Upload Images + Videos</label>
                  {images?.length > 0 && (
                    <div className="d-flex flex-wrap">
                      {images?.map((img, index) => (
                        <>
                          <span
                            key={index}
                            className="badge badge-primary mr-2"
                            style={{
                              display: "inline-blda4ock",
                              border: "solid 1px #85da4f",
                              backgroundColor: "transparent",
                              color: "#000",
                              padding: "8px 12px",
                              borderRadius: "13px",
                              fontSize: "14px",
                              margin: "5px",
                              cursor: "pointer",
                              boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                              transition: "all 0.3s ease",
                              fontWeight: "normal",
                            }}
                            onClick={() => handleRemoveImage(img)}
                          >
                            {img.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                              <img
                                src={img}
                                alt={`media-${index}`}
                                width="320"
                                height="240"
                              />
                            ) : (
                              <video width="320" height="240" controls>
                                <source src={img} type="video/mp4" />
                              </video>
                            )}
                            <span
                              style={{
                                marginLeft: "5px",
                                fontWeight: "bold",
                                color: "#ff0000",
                                fontSize: "2em",
                              }}
                            >
                              ×
                            </span>
                          </span>
                        </>
                      ))}
                    </div>
                  )}
                  <input
                    type="file"
                    className="form-control"
                    onChange={handleImageUpload}
                    multiple
                    accept="image/*,video/*,.mkv"
                  />
                </div>

                {/* Remarks */}
                <div className="col-md-12 mt-3">
                  <label>Remarks</label>
                  <textarea
                    className="form-control"
                    value={editProperty?.remarks || ""}
                    onChange={(e) =>
                      handleInputChange("remarks", e.target.value)
                    }
                    placeholder="Enter remarks (if any)"
                    rows="3"
                    disabled={isLoading}
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-3">
              <div className="row">
                {/* Date */}
                <div className="col-12 mt-3">
                  <label>
                    List By<span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    value={editProperty?.date?.split("T")[0] || ""}
                    onChange={(e) => handleInputChange("date", e.target.value)}
                    disabled={isLoading}
                    required
                  />
                </div>

                {/* Telecaller Staff */}
                <div className="col-12 mt-3">
                  <label>Telecaller Staff</label>
                  <select
                    className="form-control"
                    value={editProperty?.telecallerId || ""}
                    onChange={(e) =>
                      handleInputChange("telecallerId", e.target.value)
                    }
                    disabled={isLoading}
                  >
                    <option value="">---Select---</option>
                    {telecallingStaff.map((staff) => (
                      <option key={staff._id} value={staff._id}>
                        {`${staff.name} (${staff.designation?.name || "N/A"})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Survey Staff */}
                <div className="col-12 mt-3">
                  <label>Survey Staff</label>
                  <select
                    className="form-control"
                    value={editProperty?.surveyorId || ""}
                    onChange={(e) =>
                      handleInputChange("surveyorId", e.target.value)
                    }
                    disabled={isLoading}
                  >
                    <option value="">---Select---</option>
                    {surveyStaff.map((staff) => (
                      <option key={staff._id} value={staff._id}>
                        {`${staff.name} (${staff.designation?.name || "N/A"})`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* New entry Checkbox */}
                <div className="col-12 mt-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={isNewEntry}
                      onChange={(e) => setIsNewEntry(e.target.checked)}
                      disabled={isLoading}
                    />
                    <label
                      className="form-check-label"
                      style={{ fontWeight: "bold" }}
                    >
                      New entry
                    </label>
                  </div>
                </div>

                {/* Sales Staff */}
                {/* <div className="col-12 mt-3">
                  <label>Sales Staff</label>
                  <select
                    className="form-control"
                    value={editProperty?.salesPersonId || ""}
                    onChange={(e) =>
                      handleInputChange("salesPersonId", e.target.value)
                    }
                    disabled={isLoading}
                  >
                    <option value="">---Select---</option>
                    {salesStaff.map((staff) => (
                      <option key={staff._id} value={staff._id}>
                        {`${staff.name} (${staff.designation?.name || "N/A"})`}
                      </option>
                    ))}
                  </select>
                </div> */}

                {/* Action Buttons */}
                <div className="col-12 mt-4">
                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={isLoading || imageLoading}
                  >
                    {isLoading ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2"></i>
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Save Changes
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100 mt-3"
                    onClick={() => window.close()}
                    disabled={isLoading}
                  >
                    <i className="fas fa-times me-2"></i>
                    Cancel & Close
                  </button>

                  {/* <button
                    type="button"
                    className="btn btn-outline-info w-100 mt-3"
                    onClick={() => {
                      window.open(`/property/view/${id}`, "_blank");
                    }}
                  >
                    <i className="fas fa-eye me-2"></i>
                    View Property
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProperty;
